import type { ExecutionLog } from '@/types';

/**
 * Execution status after normalization
 */
export type ExecutionStatus = 'running' | 'success' | 'partial' | 'error';

/**
 * Result of status determination with additional context
 */
export interface ExecutionStatusInfo {
  status: ExecutionStatus;
  tooltip: string;
  color: 'success' | 'error' | 'warning' | 'info';
}

/**
 * Normalize and determine execution status
 * Handles inconsistent backend responses (boolean, string, number)
 *
 * @param execution - ExecutionLog to analyze
 * @returns Normalized status information
 */
export const getExecutionStatus = (execution: ExecutionLog): ExecutionStatusInfo => {
  // Check running state first
  if (execution.running || (!execution.completed && !execution.succeeded)) {
    return {
      status: 'running',
      tooltip: 'Running',
      color: 'info',
    };
  }

  // Normalize succeeded value to handle inconsistent backend responses
  // Backend can send: true, "true", 1, false, "false", 0, or "partial"
  const succeededValue = String(execution.succeeded).toLowerCase();
  const isSuccess = succeededValue === 'true' || succeededValue === '1';
  const isPartialSuccess =
    succeededValue === 'partial' ||
    (execution.statusText && execution.statusText.toLowerCase().includes('partial'));

  // Determine status
  if (isPartialSuccess) {
    return {
      status: 'partial',
      tooltip: 'Partial Success',
      color: 'warning',
    };
  }

  if (execution.completed && isSuccess) {
    return {
      status: 'success',
      tooltip: 'Success',
      color: 'success',
    };
  }

  // Error/Failed state
  return {
    status: 'error',
    tooltip: execution.errorMessage || 'Failed',
    color: 'error',
  };
};
